/**
 * Autore: Salvo de Luca
 * Azienda Dialogicom s.r.l.s.
 * File: src/components/modular-form/CustomerRegistrationForm.tsx
 * Release: 1.0.0
 * Descrizione: Form per la registrazione e modifica dei profili cliente
 */

import { appAlert } from '../../lib/appDialog';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { ModularForm } from './ModularForm';
import { customerRegistrationConfig } from './configs/customerRegistrationConfigs';
import { ProfilePhotoInputWithRemote } from '../mobile-operators/ProfilePhotoInputWithRemote';
import { useDevSimulatorForm, type SimulatorFormConfig, type ApplyDataOptions } from '../dev/DevSimulatorContext';
import { useSede, SEDI, type SedeType } from '../../contexts/SedeContext';
import { MapPin, Save } from 'lucide-react';
import { CustomerQRSection } from './components/CustomerQRSection';
import { EmailChangeModal } from '../auth/EmailChangeModal';
import MissingFieldsWarningModal from '../shared/MissingFieldsWarningModal';

// La modale dei campi mancanti era duplicata qui e in AccettazioneTurbineModal:
// stesso testo, stesse due scelte, due implementazioni da tenere allineate a
// mano. Ora è una sola, in components/shared/MissingFieldsWarningModal.

/**
 * Parsing automatico di indirizzi italiani scritti manualmente in vari formati.
 * Estrae: via, numeroCivico, cap, citta, provincia
 *
 * Formati supportati:
 * - "Via Roma, 123 - 00100 Roma (RM)"
 * - "Largo Marconi 135, 10126 Acireale CT"
 * - "Via Etnea 789 95100 Catania"
 * - "Piazza Verdi, 45 20100 Milano MI"
 * - "Via Roma 123 Catania"
 */
const parseItalianAddress = (addressString: string): {
  via: string;
  numeroCivico: string;
  cap: string;
  citta: string;
  provincia: string;
  parsed: boolean;
} => {
  const result = {
    via: '',
    numeroCivico: '',
    cap: '',
    citta: '',
    provincia: '',
    parsed: false
  };

  if (!addressString || typeof addressString !== 'string') {
    return result;
  }

  let address = addressString.trim();
  console.log('📍 [parseItalianAddress] Input:', address);

  // 1. Estrai PROVINCIA - formato "(XX)" o "XX" alla fine (2 lettere maiuscole)
  const provinciaInParenMatch = address.match(/\(([A-Z]{2})\)\s*$/i);
  if (provinciaInParenMatch) {
    result.provincia = provinciaInParenMatch[1].toUpperCase();
    address = address.replace(/\s*\([A-Z]{2}\)\s*$/i, '').trim();
  } else {
    // Provincia senza parentesi alla fine (es. "Catania CT")
    const provinciaEndMatch = address.match(/\s+([A-Z]{2})\s*$/i);
    if (provinciaEndMatch) {
      result.provincia = provinciaEndMatch[1].toUpperCase();
      address = address.replace(/\s+[A-Z]{2}\s*$/i, '').trim();
    }
  }

  // 2. Estrai CAP - 5 cifre consecutive
  const capMatch = address.match(/\b(\d{5})\b/);
  if (capMatch) {
    result.cap = capMatch[1];
    address = address.replace(/\s*-?\s*\d{5}\s*-?\s*/, ' ').trim();
  }

  // 3. Normalizza separatori
  address = address
    .replace(/\s*-\s*/g, ', ')  // trattini → virgole
    .replace(/\s+/g, ' ')       // spazi multipli → singolo
    .replace(/,\s*,/g, ',')     // virgole doppie → singola
    .trim();

  // 4. Tipi di via riconosciuti
  const streetTypes = [
    'Via', 'Viale', 'Piazza', 'Piazzale', 'Corso', 'Largo', 'Vicolo',
    'Strada', 'Contrada', 'Traversa', 'Salita', 'Discesa', 'Lungomare',
    'Via Privata', 'Vico', 'Borgo', 'Calata', 'Galleria', 'Passaggio'
  ];
  const streetTypePattern = streetTypes.join('|');

  // 5. Pattern per estrarre via + numero civico
  // Match: "Via Nome, 123" oppure "Via Nome 123" oppure "Via Nome n.123" oppure "Via Nome n° 123"
  const streetWithNumberRegex = new RegExp(
    `^((?:${streetTypePattern})\\s+[^,\\d]+?)(?:,\\s*|\\s+)(?:n\\.?°?\\s*)?(\\d+[a-zA-Z]?)(?:[,\\s]|$)`,
    'i'
  );
  const streetMatch = address.match(streetWithNumberRegex);

  if (streetMatch) {
    result.via = streetMatch[1].trim();
    result.numeroCivico = streetMatch[2].trim();
    // Rimuovi la parte estratta per trovare la città
    address = address.replace(streetWithNumberRegex, '').trim();
    // Pulisci virgole iniziali
    address = address.replace(/^[,\s]+/, '').trim();
  } else {
    // Fallback: prova a estrarre solo il tipo di via senza numero
    const streetOnlyRegex = new RegExp(`^((?:${streetTypePattern})\\s+[^,]+?)(?:,|$)`, 'i');
    const streetOnlyMatch = address.match(streetOnlyRegex);
    if (streetOnlyMatch) {
      result.via = streetOnlyMatch[1].trim();
      address = address.replace(streetOnlyRegex, '').trim();
      address = address.replace(/^[,\s]+/, '').trim();
    }
  }

  // 6. Estrai numero civico se ancora presente nell'indirizzo residuo
  if (!result.numeroCivico) {
    const numMatch = address.match(/^(?:n\\.?°?\\s*)?(\\d+[a-zA-Z]?)(?:[,\\s]|$)/);
    if (numMatch) {
      result.numeroCivico = numMatch[1];
      address = address.replace(/^(?:n\.?°?\s*)?\d+[a-zA-Z]?[,\s]*/, '').trim();
    }
  }

  // 7. Il resto è la città (pulisci virgole e spazi)
  address = address.replace(/^[,\s]+|[,\s]+$/g, '').trim();
  if (address) {
    result.citta = address;
  }

  // Determina se il parsing ha avuto successo (almeno via O città estratti)
  result.parsed = !!(result.via || result.citta);

  console.log('📍 [parseItalianAddress] Result:', result);
  return result;
};

interface CustomerRegistrationFormProps {
  userId: string;
  initialData?: Record<string, any>;
  onSuccess: (message: string, savedUserId?: string) => void;
  onError: (error: any) => void;
  showTypeSwitch: boolean;
  defaultType: 'azienda' | 'privato';
  readOnly?: boolean;
  onPhotoChange?: (photoUrl: string | null) => void; // Callback per sincronizzare la foto con il parent
  /** Callback per sincronizzare nome/tipo nell'header del parent quando l'utente cambia sezione */
  onHeaderSync?: (data: { displayName: string; customerType: 'company' | 'individual' }) => void;
  hideSubmitButton?: boolean;
}

export const CustomerRegistrationForm: React.FC<CustomerRegistrationFormProps> = ({
  userId,
  initialData = {},
  onSuccess,
  onError,
  showTypeSwitch,
  defaultType,
  readOnly = false,
  onPhotoChange,
  onHeaderSync,
  hideSubmitButton,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});

  // === EMAIL CHANGE MODAL STATE ===
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    oldEmail: string;
    newEmail: string;
    pendingFormValues: Record<string, any>;
  } | null>(null);
  const originalEmailRef = useRef<string | null>(null);

  // State per modal campi mancanti
  const [missingFieldsModal, setMissingFieldsModal] = useState<{
    isOpen: boolean;
    fields: string[];
    resolve: ((value: boolean) => void) | null;
  }>({ isOpen: false, fields: [], resolve: null });

  // State per warning sede mismatch
  const [showSedeWarning, setShowSedeWarning] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState<Record<string, any> | null>(null);

  // Handler per campi required mancanti (mostra popup)
  const handleMissingRequiredFields = (missingFields: string[]): Promise<boolean> => {
    return new Promise((resolve) => {
      setMissingFieldsModal({
        isOpen: true,
        fields: missingFields,
        resolve
      });
    });
  };

  const handleModalConfirm = () => {
    missingFieldsModal.resolve?.(true);
    setMissingFieldsModal({ isOpen: false, fields: [], resolve: null });
  };

  const handleModalCancel = () => {
    missingFieldsModal.resolve?.(false);
    setMissingFieldsModal({ isOpen: false, fields: [], resolve: null });
  };
  
  // Debug: traccia quando formData viene resettato
  const setFormDataWithLog = (newData: any) => {
    if (typeof newData === 'function') {
      setFormData((prev) => {
        const next = newData(prev);
        if (Object.keys(prev).length > 0 && Object.keys(next).length === 0) {
          console.error('⚠️ [CustomerRegistrationForm] ATTENZIONE: formData sta per essere resettato a vuoto!', 
            '\nPrev:', prev, 
            '\nNext:', next,
            '\nStack:', new Error().stack);
        }
        return next;
      });
    } else {
      if (Object.keys(formData).length > 0 && Object.keys(newData).length === 0) {
        console.error('⚠️ [CustomerRegistrationForm] ATTENZIONE: formData sta per essere resettato a vuoto!', 
          '\nCurrent:', formData, 
          '\nNew:', newData,
          '\nStack:', new Error().stack);
      }
      setFormData(newData);
    }
  };
  // Inizializza il tipo form - priorità: initialData > SessionStorage > defaultType
  const getInitialFormType = (): 'azienda' | 'privato' => {
    // PRIORITÀ 1: Se c'è un valore esplicito in initialData (es. da DevSimulator o Transfer), usalo
    // Supporta sia customer_type (snake_case) che customerType (camelCase)
    // Supporta sia valori 'company'/'individual' che 'azienda'/'privato'
    const typeValue = initialData?.customer_type || initialData?.customerType;
    if (typeValue) {
      let dbType: 'azienda' | 'privato';
      if (typeValue === 'company' || typeValue === 'azienda') {
        dbType = 'azienda';
      } else {
        dbType = 'privato';
      }
      console.log(`🔄 [CustomerRegistrationForm] Recuperato da initialData: ${typeValue} → ${dbType}`);
      // Aggiorna anche SessionStorage per coerenza
      sessionStorage.setItem(`toggleState_${userId}`, dbType);
      return dbType;
    }

    // PRIORITÀ 2: SessionStorage per persistenza scelta utente
    const savedType = sessionStorage.getItem(`toggleState_${userId}`);
    if (savedType === 'azienda' || savedType === 'privato') {
      console.log(`🔄 [CustomerRegistrationForm] Recuperato da SessionStorage toggleState_${userId}: ${savedType}`);
      return savedType;
    }

    // PRIORITÀ 3: Fallback al default
    console.log(`🔄 [CustomerRegistrationForm] Usando tipo default: ${defaultType}`);
    return defaultType;
  };
  
  const [currentFormType, setCurrentFormType] = useState<'azienda' | 'privato'>(getInitialFormType());
  const { getToken, user: currentUser } = useAuth();
  const { sedeCorrente, isLoading: isSedeLoading } = useSede(); // Per sincronizzare con header dropdown

  // Flag per tracciare se il form è già stato inizializzato con i default
  const isInitializedRef = useRef<boolean>(false);

  // Determina se l'utente è un cliente (per decidere se applicare validazione strict)
  const isClientUser = currentUser?.role === 'client';

  // Debug log per tracciare formData
  console.log('🔍 [CustomerRegistrationForm] Current formData:', formData);
  console.log('🔍 [CustomerRegistrationForm] initialData:', initialData);
  console.log('🔍 [CustomerRegistrationForm] currentUser from AuthProvider:', currentUser);

  const mapDbToFormData = (db: Record<string, any>): Record<string, any> => {
    if (!db) return {};

    // 🔍 DEBUG: Cosa arriva dal backend per payment/delivery/privacy?
    console.log('🔍 [mapDbToFormData] INPUT RAW - payment_methods:', db.payment_methods, 'tipo:', typeof db.payment_methods);
    console.log('🔍 [mapDbToFormData] INPUT RAW - delivery_methods:', db.delivery_methods, 'tipo:', typeof db.delivery_methods);
    console.log('🔍 [mapDbToFormData] INPUT RAW - privacy_policy:', db.privacy_policy, 'dichiarazione_veridicita:', db.dichiarazione_veridicita);

    // Gestisce indirizzo sia come oggetto (da transfer) che da campi DB
    let indirizzo = { search: '', via: '', numeroCivico: '', citta: '', cap: '', provincia: '', paese: 'Italia' } as any;

    console.log('🏠 [mapDbToFormData] DEBUG INDIRIZZO - Input:', {
      'db.indirizzo': db.indirizzo,
      'db.extended_data': db.extended_data,
      'db.via': db.via,
      'db.civico': db.civico
    });

    // Se db.indirizzo è già un oggetto (da transfer), usalo come base
    if (db.indirizzo && typeof db.indirizzo === 'object') {
      indirizzo = { ...indirizzo, ...db.indirizzo };
      console.log('🏠 [mapDbToFormData] Indirizzo da oggetto diretto:', indirizzo);
      // FIX: Anche se db.indirizzo esiste, fai fallback sui campi flat se numeroCivico è vuoto
      if (!indirizzo.numeroCivico && db.civico) {
        indirizzo.numeroCivico = db.civico;
        console.log('🏠 [mapDbToFormData] Civico recuperato da db.civico:', db.civico);
      }
    } else {
      // Altrimenti prova extended_data (da DB)
      if (db.extended_data) {
        try {
          const ext = typeof db.extended_data === 'string' ? JSON.parse(db.extended_data) : db.extended_data;
          if (ext && ext.indirizzo) {
            indirizzo = { ...indirizzo, ...ext.indirizzo };
            console.log('🏠 [mapDbToFormData] Indirizzo da extended_data:', indirizzo);
          }
        } catch (e) {
          console.error('🏠 [mapDbToFormData] Errore parsing extended_data:', e);
        }
      }

      // Verifica se ci sono campi separati (cap, citta, provincia) o solo indirizzo come stringa
      const hasSeparateFields = db.cap || db.citta || db.provincia;
      const hasFullAddressString = db.indirizzo && typeof db.indirizzo === 'string' && db.indirizzo.trim();
      // db.via è alias di db.indirizzo nel backend per clienti temporanei
      const hasViaAsFullAddress = db.via && typeof db.via === 'string' && db.via.trim() && !hasSeparateFields;

      if ((hasFullAddressString || hasViaAsFullAddress) && !hasSeparateFields) {
        // Indirizzo è una stringa unica senza campi separati (es. da business_card_temp)
        const fullAddress = db.indirizzo || db.via;

        // Prova parsing automatico dell'indirizzo italiano
        const parsed = parseItalianAddress(fullAddress);

        if (parsed.parsed) {
          // Parsing riuscito - popola i campi estratti
          indirizzo.via = parsed.via || '';
          indirizzo.numeroCivico = parsed.numeroCivico || '';
          indirizzo.cap = parsed.cap || '';
          indirizzo.citta = parsed.citta || '';
          indirizzo.provincia = parsed.provincia || '';
          // Mantieni anche nel campo search per eventuali correzioni manuali via Google Maps
          indirizzo.search = fullAddress;
          console.log('🏠 [mapDbToFormData] Indirizzo PARSED automaticamente:', indirizzo);
        } else {
          // Parsing fallito - metti solo in search per ricerca manuale Google Maps
          indirizzo.search = fullAddress;
          console.log('🏠 [mapDbToFormData] Parsing fallito, indirizzo → campo search:', fullAddress);
        }
      } else {
        // Fallback su colonne piane (campi separati disponibili)
        indirizzo = {
          ...indirizzo,
          via: db.via || indirizzo.via,
          numeroCivico: db.civico || indirizzo.numeroCivico,
          citta: db.citta || indirizzo.citta,
          cap: db.cap || indirizzo.cap,
          provincia: db.provincia || indirizzo.provincia,
          paese: db.paese || indirizzo.paese,
        };
        console.log('🏠 [mapDbToFormData] Indirizzo da campi separati:', indirizzo);
      }
    }

    // Converti tipo cliente: supporta 'company'/'individual' e 'azienda'/'privato'
    const rawType = db.customer_type || db.customerType || (db.company_name || db.ragioneSociale ? 'company' : 'individual');
    const normalizedType = (rawType === 'company' || rawType === 'azienda') ? 'company' : 'individual';

    const result = {
      // Tipo cliente e base - normalizzato a 'company'/'individual'
      customer_type: normalizedType,
      customer_code: db.customer_code || null, // Codice cliente univoco (da temp o definitivo)
      email: db.email || '',

      // Foto profilo cliente (da transfer pending customers)
      fotoProfilo: db.fotoProfilo || db.foto_profilo || db.foto_profilo_cliente || null,

      // Dati azienda - mappatura corretta ai campi DB e transfer
      ragioneSociale: db.ragioneSociale || db.company_name || '',
      prefissoNazione: db.prefissoNazione || db.country_prefix || 'IT',
      partitaIva: db.partitaIva || db.vat_number || '',
      codiceUnivoco: db.codiceUnivoco || db.unique_code || '',
      pec: db.pec || '',

      // Dati privato - supporta sia formato transfer che DB
      titolo: db.titolo || db.title || '',
      nome: db.nome || db.first_name || '',
      cognome: db.cognome || db.last_name || '',
      codiceFiscale: db.codiceFiscale || db.tax_code || '',

      // Telefono azienda/privato - supporta sia formato transfer che DB
      prefissoTelefono: db.prefissoTelefono || db.phone_country_code || '+39',
      telefono: db.telefono || db.phone_number || '',
      social: { whatsapp: !!db.phone_whatsapp, telegram: !!db.phone_telegram },
      telefonoSocial: { whatsapp: !!db.phone_whatsapp, telegram: !!db.phone_telegram },

      // REFERENTI - Mappatura dai campi reali del database E transfer
      // Referente principale (per azienda) / unico (per privato) - da referente1_* o transfer
      referentePrincipaleTitolo: db.referentePrincipaleTitolo || db.referente1_titolo || '',
      referentePrincipaleNome: db.referentePrincipaleNome || db.referente1_nome || '',
      referentePrincipaleCognome: db.referentePrincipaleCognome || db.referente1_cognome || '',
      referentePrincipalePrefissoFisso: db.referentePrincipalePrefissoFisso || db.referente1_prefisso_fisso || '+39',
      referentePrincipaleTelefono: db.referentePrincipaleTelefono || db.referente1_telefono || '',
      referentePrincipalePrefissoCellulare: db.referentePrincipalePrefissoCellulare || db.referente1_prefisso_cellulare || '+39',
      referentePrincipaleCellulare: db.referentePrincipaleCellulare || db.referente1_cellulare || '',
      referentePrincipaleSocial: {
        whatsapp: !!db.referente1_whatsapp,
        telegram: !!db.referente1_telegram
      },
      referentePrincipaleEmail: db.referentePrincipaleEmail || db.referente1_email || '', // Email referente principale

      // Referente secondario (solo per azienda) - da referente2_*
      referenteSecondarioTitolo: db.referente2_titolo || '',
      referenteSecondarioNome: db.referente2_nome || '',
      referenteSecondarioCognome: db.referente2_cognome || '',
      referenteSecondarioPrefissoFisso: db.referente2_telefono_prefisso || '+39',
      referenteSecondarioTelefono: db.referente2_telefono || '',
      referenteSecondarioPrefissoCellulare: db.referente2_cellulare_prefisso || '+39',
      referenteSecondarioCellulare: db.referente2_cellulare || '',
      referenteSecondarioSocial: {
        whatsapp: !!db.referente2_whatsapp,
        telegram: !!db.referente2_telegram
      },
      referenteSecondarioEmail: db.referente2_email || '',

      // Referente privato (solo per privato) - da referente1_* 
      referentePrivatoTitolo: db.referente1_titolo || '',
      referentePrivatoNome: db.referente1_nome || '',
      referentePrivatoCognome: db.referente1_cognome || '',
      referentePrivatoPrefissoFisso: db.referente1_telefono_prefisso || '+39',
      referentePrivatoTelefonoFisso: db.referente1_telefono || '',
      referentePrivatoPrefissoCellulare: db.referente1_cellulare_prefisso || '+39',
      referentePrivatoCellulare: db.referente1_cellulare || '',
      referentePrivatoSocial: {
        whatsapp: !!db.referente1_whatsapp,
        telegram: !!db.referente1_telegram
      },
      referentePrivatoEmail: db.referente1_email || '', // Email referente privato

      // Meccanico di fiducia
      meccanicoRagioneSociale: db.meccanico_ragione_sociale || '',
      meccanicoNome: db.meccanico_nome || '',
      meccanicoCognome: db.meccanico_cognome || '',
      meccanicoEmail: db.meccanico_email || '',
      meccanicoWebsite: db.meccanico_website || '',
      meccanicoPrefissoTelefono: db.meccanico_prefisso_telefono || '+39',
      meccanicoTelefono: db.meccanico_telefono || '',
      meccanicoSocial: {
        whatsapp: !!db.meccanico_whatsapp,
        telegram: !!db.meccanico_telegram
      },
      meccanicoIndirizzo: {
        via: db.meccanico_via || '',
        numeroCivico: db.meccanico_civico || '',
        cap: db.meccanico_cap || '',
        citta: db.meccanico_citta || '',
        provincia: db.meccanico_provincia || '',
        paese: 'Italia',
        lat: db.meccanico_lat || undefined,
        lng: db.meccanico_lng || undefined
      },

      // Indirizzo
      indirizzo,
      noteIndirizzo: db.noteIndirizzo || db.note || db.note_indirizzo || '',

      // Metodi di pagamento (oggetto completo)
      // Supporta sia camelCase (da simulatore) che snake_case (da DB)
      paymentMethods: (() => {
        // Se già passato come oggetto (es. da DevSimulator), usalo direttamente
        if (db.paymentMethods && typeof db.paymentMethods === 'object' && !Array.isArray(db.paymentMethods)) {
          console.log('✅ [mapDbToFormData] paymentMethods da oggetto diretto:', db.paymentMethods);
          // FIX: Controlla se è double-nested (methods è un oggetto con methods array dentro)
          if (db.paymentMethods.methods && typeof db.paymentMethods.methods === 'object' && !Array.isArray(db.paymentMethods.methods) && Array.isArray(db.paymentMethods.methods.methods)) {
            console.log('🔧 [mapDbToFormData] RILEVATA struttura double-nested paymentMethods, appiattisco');
            return db.paymentMethods.methods; // Restituisci il livello interno
          }
          return db.paymentMethods;
        }
        try {
          console.log('🔧 [mapDbToFormData] Parsing payment_methods:', db.payment_methods);
          const parsed = db.payment_methods ? JSON.parse(db.payment_methods) : [];
          console.log('🔧 [mapDbToFormData] Parsed payment_methods:', parsed, 'isArray:', Array.isArray(parsed));
          // Se è un array semplice, lo trasformiamo in oggetto completo
          if (Array.isArray(parsed)) {
            const preferences = db.payment_preferences ? JSON.parse(db.payment_preferences) : {};
            return {
              methods: parsed,
              preferences: { ...preferences, notes: db.payment_notes || '' },
              notes: db.payment_notes || ''
            };
          }
          // FIX: Controlla se è double-nested (methods è un oggetto con methods array dentro)
          if (parsed.methods && typeof parsed.methods === 'object' && !Array.isArray(parsed.methods) && Array.isArray(parsed.methods.methods)) {
            console.log('🔧 [mapDbToFormData] RILEVATA struttura double-nested da DB, appiattisco');
            return parsed.methods; // Restituisci il livello interno
          }
          // Se è già un oggetto completo con methods come array, lo usiamo così com'è
          console.log('🔧 [mapDbToFormData] Returning payment_methods object:', parsed);
          return parsed;
        } catch (err) {
          console.error('❌ [mapDbToFormData] ERRORE parsing payment_methods:', err, 'input:', db.payment_methods);
          return {
            methods: [],
            preferences: { notes: '' },
            notes: ''
          };
        }
      })(),

      // Metodi di consegna (oggetto completo)
      // Supporta sia camelCase (da simulatore) che snake_case (da DB)
      deliveryMethods: (() => {
        // Se già passato come oggetto (es. da DevSimulator), usalo direttamente
        if (db.deliveryMethods && typeof db.deliveryMethods === 'object' && !Array.isArray(db.deliveryMethods)) {
          console.log('✅ [mapDbToFormData] deliveryMethods da oggetto diretto:', db.deliveryMethods);
          // FIX: Controlla se è double-nested (methods è un oggetto con methods array dentro)
          if (db.deliveryMethods.methods && typeof db.deliveryMethods.methods === 'object' && !Array.isArray(db.deliveryMethods.methods) && Array.isArray(db.deliveryMethods.methods.methods)) {
            console.log('🔧 [mapDbToFormData] RILEVATA struttura double-nested deliveryMethods, appiattisco');
            return db.deliveryMethods.methods; // Restituisci il livello interno
          }
          return db.deliveryMethods;
        }
        try {
          console.log('🔧 [mapDbToFormData] Parsing delivery_methods:', db.delivery_methods);
          const parsed = db.delivery_methods ? JSON.parse(db.delivery_methods) : [];
          console.log('🔧 [mapDbToFormData] Parsed delivery_methods:', parsed, 'isArray:', Array.isArray(parsed));
          // Se è un array semplice, lo trasformiamo in oggetto completo
          if (Array.isArray(parsed)) {
            const preferences = db.delivery_preferences ? JSON.parse(db.delivery_preferences) : {};
            const address = db.delivery_address ? JSON.parse(db.delivery_address) : {};
            return {
              methods: parsed,
              preferences: {
                ...preferences,
                preferredAddress: address?.preferredAddress || '',
                timeSlot: preferences?.timeSlot || '',
                notes: db.delivery_notes || ''
              },
              address: address?.preferredAddress || '',
              timeSlot: preferences?.timeSlot || '',
              notes: db.delivery_notes || ''
            };
          }
          // FIX: Controlla se è double-nested (methods è un oggetto con methods array dentro)
          if (parsed.methods && typeof parsed.methods === 'object' && !Array.isArray(parsed.methods) && Array.isArray(parsed.methods.methods)) {
            console.log('🔧 [mapDbToFormData] RILEVATA struttura double-nested delivery da DB, appiattisco');
            return parsed.methods; // Restituisci il livello interno
          }
          // Se è già un oggetto completo con methods come array, lo usiamo così com'è
          console.log('🔧 [mapDbToFormData] Returning delivery_methods object:', parsed);
          return parsed;
        } catch (err) {
          console.error('❌ [mapDbToFormData] ERRORE parsing delivery_methods:', err, 'input:', db.delivery_methods);
          return {
            methods: [],
            preferences: {
              preferredAddress: '',
              timeSlot: '',
              notes: ''
            },
            address: '',
            timeSlot: '',
            notes: ''
          };
        }
      })(),

      // Sede preferita - usa sedeCorrente come fallback se non impostato nel DB
      sede_preferita: db.sede_preferita || sedeCorrente,

      // Privacy
      privacyAccepted: !!db.privacy_policy,
      dichiarazioneVeridicita: !!db.dichiarazione_veridicita,
    };
    
    console.log('✅ [mapDbToFormData] Risultato mapping:', JSON.stringify(result, null, 2));
    console.log('✅ [mapDbToFormData] paymentMethods dopo mapping:', result.paymentMethods);
    console.log('✅ [mapDbToFormData] deliveryMethods dopo mapping:', result.deliveryMethods);
    console.log('✅ [mapDbToFormData] privacy dopo mapping - privacyAccepted:', result.privacyAccepted, 'dichiarazioneVeridicita:', result.dichiarazioneVeridicita);
    return result;
  };

  const mapFormToDbData = (values: Record<string, any>): Record<string, any> => {
    if (!values || typeof values !== 'object') {
      console.error('❌ [mapFormToDbData] values è null, undefined o non è un oggetto:', values);
      throw new Error('Dati del form non validi');
    }
    const indirizzo = values.indirizzo || {};
    
    // Usa il tipo di form corrente invece di fare affidamento sui campi
    const customerType = currentFormType === 'azienda' ? 'company' : 'individual';
    console.log('🔍 [mapFormToDbData] currentFormType:', currentFormType, '-> customer_type:', customerType);
    
    const result = {
      customer_type: customerType,
      customer_code: values.customer_code || null, // Preserva codice esistente (da temp)
      email: values.email || '',

      // Dati azienda - solo campi diretti (il database ora ha le colonne con questi nomi)
      ragioneSociale: values.ragioneSociale || null,
      prefissoNazione: values.prefissoNazione || 'IT',
      partitaIva: values.partitaIva || null,
      unique_code: values.codiceUnivoco || null,
      pec: values.pec || null,

      // Dati privato
      title: values.titolo || null,
      first_name: values.nome || null,
      last_name: values.cognome || null,
      tax_code: values.codiceFiscale || null,

      // Telefono
      phone_country_code: values.prefissoTelefono || '+39',
      phone_number: values.telefono || null,
      // Il form AZIENDA usa 'telefonoSocial', il form PRIVATO usa 'social' (vedi mapping di load).
      // Bug: la vecchia logica preferiva sempre telefonoSocial (caricato dal DB ma non toccato
      // dalla checkbox del privato) → il flag WhatsApp del cliente privato non si salvava.
      phone_whatsapp: customerType === 'company' ? !!values.telefonoSocial?.whatsapp : !!values.social?.whatsapp,
      phone_telegram: customerType === 'company' ? !!values.telefonoSocial?.telegram : !!values.social?.telegram,

      // Campi azienda - mappatura ai nomi corretti del DB
      company_name: values.ragioneSociale || null,
      vat_number: values.partitaIva || null,
      country_prefix: values.prefissoNazione || 'IT',

      // REFERENTI - Mappatura ai campi frontend attesi dal backend
      // Referente principale (per aziende) o privato
      referentePrincipaleTitolo: customerType === 'company' ? values.referentePrincipaleTitolo : values.referentePrivatoTitolo,
      referentePrincipaleNome: customerType === 'company' ? values.referentePrincipaleNome : values.referentePrivatoNome,
      referentePrincipaleCognome: customerType === 'company' ? values.referentePrincipaleCognome : values.referentePrivatoCognome,
      referentePrincipalePrefissoFisso: customerType === 'company' ? values.referentePrincipalePrefissoFisso : values.referentePrivatoPrefissoFisso,
      referentePrincipaleTelefono: customerType === 'company' ? values.referentePrincipaleTelefono : values.referentePrivatoTelefonoFisso,
      referentePrincipalePrefissoCellulare: customerType === 'company' ? (values.referentePrincipalePrefissoCellulare || '+39') : (values.referentePrivatoPrefissoCellulare || '+39'),
      referentePrincipaleCellulare: customerType === 'company' ? values.referentePrincipaleCellulare : values.referentePrivatoCellulare,
      referentePrincipaleWhatsapp: customerType === 'company' ? !!(values.referentePrincipaleSocial?.whatsapp) : !!(values.referentePrivatoSocial?.whatsapp),
      referentePrincipaleTelegram: customerType === 'company' ? !!(values.referentePrincipaleSocial?.telegram) : !!(values.referentePrivatoSocial?.telegram),
      
      // Referente 2 (secondario per azienda) - CON EMAIL nel DB
      referente2_titolo: customerType === 'company' ? values.referenteSecondarioTitolo : null,
      referente2_nome: customerType === 'company' ? values.referenteSecondarioNome : null,
      referente2_cognome: customerType === 'company' ? values.referenteSecondarioCognome : null,
      referente2_email: customerType === 'company' ? values.referenteSecondarioEmail : null,
      referente2_telefono_prefisso: customerType === 'company' ? values.referenteSecondarioPrefissoFisso : null,
      referente2_telefono: customerType === 'company' ? values.referenteSecondarioTelefono : null,
      referente2_cellulare_prefisso: customerType === 'company' ? values.referenteSecondarioPrefissoCellulare : null,
      referente2_cellulare: customerType === 'company' ? values.referenteSecondarioCellulare : null,
      referente2_whatsapp: customerType === 'company' ? !!(values.referenteSecondarioSocial?.whatsapp) : null,
      referente2_telegram: customerType === 'company' ? !!(values.referenteSecondarioSocial?.telegram) : null,

      // Referente 1 (principale/privato) - Mapping ai campi DB
      referente1_titolo: customerType === 'company' ? values.referentePrincipaleTitolo : values.referentePrivatoTitolo,
      referente1_nome: customerType === 'company' ? values.referentePrincipaleNome : values.referentePrivatoNome,
      referente1_cognome: customerType === 'company' ? values.referentePrincipaleCognome : values.referentePrivatoCognome,
      referente1_email: customerType === 'company' ? values.referentePrincipaleEmail : values.referentePrivatoEmail,
      referente1_telefono_prefisso: customerType === 'company' ? values.referentePrincipalePrefissoFisso : values.referentePrivatoPrefissoFisso,
      referente1_telefono: customerType === 'company' ? values.referentePrincipaleTelefono : values.referentePrivatoTelefonoFisso,
      referente1_cellulare_prefisso: customerType === 'company' ? values.referentePrincipalePrefissoCellulare : values.referentePrivatoPrefissoCellulare,
      referente1_cellulare: customerType === 'company' ? values.referentePrincipaleCellulare : values.referentePrivatoCellulare,
      referente1_whatsapp: customerType === 'company' ? !!(values.referentePrincipaleSocial?.whatsapp) : !!(values.referentePrivatoSocial?.whatsapp),
      referente1_telegram: customerType === 'company' ? !!(values.referentePrincipaleSocial?.telegram) : !!(values.referentePrivatoSocial?.telegram),

      // Meccanico di fiducia
      meccanico_ragione_sociale: values.meccanicoRagioneSociale || null,
      meccanico_nome: values.meccanicoNome || null,
      meccanico_cognome: values.meccanicoCognome || null,
      meccanico_email: values.meccanicoEmail || null,
      meccanico_website: values.meccanicoWebsite || null,
      meccanico_prefisso_telefono: values.meccanicoPrefissoTelefono || '+39',
      meccanico_telefono: values.meccanicoTelefono || null,
      meccanico_whatsapp: !!(values.meccanicoSocial?.whatsapp),
      meccanico_telegram: !!(values.meccanicoSocial?.telegram),
      meccanico_via: values.meccanicoIndirizzo?.via || null,
      meccanico_civico: values.meccanicoIndirizzo?.numeroCivico || null,
      meccanico_cap: values.meccanicoIndirizzo?.cap || null,
      meccanico_citta: values.meccanicoIndirizzo?.citta || null,
      meccanico_provincia: values.meccanicoIndirizzo?.provincia || null,
      meccanico_lat: values.meccanicoIndirizzo?.lat || null,
      meccanico_lng: values.meccanicoIndirizzo?.lng || null,

      // Indirizzo: salviamo in extended_data
      extended_data: JSON.stringify({ indirizzo }),
      via: indirizzo.via || null,
      civico: indirizzo.numeroCivico || null,
      cap: indirizzo.cap || null,
      citta: indirizzo.citta || null,
      provincia: indirizzo.provincia || null,
      lat: indirizzo.lat || null,
      lng: indirizzo.lng || null,

      note: values.noteIndirizzo || values.note || null,

      // Foto profilo cliente (da transfer pending customers)
      foto_profilo: values.fotoProfilo || null,

      // Metodi di pagamento (oggetto completo)
      payment_methods: values.paymentMethods || {},
      
      // Metodi di consegna (oggetto completo)
      delivery_methods: values.deliveryMethods || {},
      
      privacy_policy: !!values.privacyAccepted,
      dichiarazione_veridicita: !!values.dichiarazioneVeridicita,

      // Sede preferita
      sede_preferita: values.sede_preferita || null,
    };
    
    // Filtra i campi per evitare che campi obsoleti vengano inviati al backend
    // IMPORTANTE: Basato sullo schema DB reale - ora referente1_email E referente2_email ESISTONO
    const allowedFields = [
      'customer_type', 'customer_code', 'email', 'ragioneSociale', 'partitaIva', 'prefissoNazione', 'unique_code', 'pec',
      'title', 'first_name', 'last_name', 'tax_code', 'phone_country_code', 'phone_number', 'phone_whatsapp', 'phone_telegram',
      'company_name', 'vat_number', 'country_prefix',
      // Referente 1 (CON email - aggiunto nel DB)
      'referente1_nome', 'referente1_cognome', 'referente1_email', 'referente1_telefono', 'referente1_cellulare', 'referente1_titolo',
      'referente1_telefono_prefisso', 'referente1_cellulare_prefisso', 'referente1_whatsapp', 'referente1_telegram',
      // Referente 2 (CON email - esiste nel DB)
      'referente2_nome', 'referente2_cognome', 'referente2_titolo', 'referente2_email',
      'referente2_telefono_prefisso', 'referente2_telefono', 'referente2_cellulare_prefisso', 'referente2_cellulare',
      'referente2_whatsapp', 'referente2_telegram',
      // Meccanico - AGGIUNTO website, prefisso_telefono, whatsapp, telegram, lat, lng
      'meccanico_ragione_sociale', 'meccanico_nome', 'meccanico_cognome', 'meccanico_email', 'meccanico_telefono',
      'meccanico_website', 'meccanico_prefisso_telefono', 'meccanico_whatsapp', 'meccanico_telegram',
      'meccanico_via', 'meccanico_civico', 'meccanico_cap', 'meccanico_citta', 'meccanico_provincia',
      'meccanico_lat', 'meccanico_lng',
      // Altri campi
      'extended_data', 'via', 'civico', 'cap', 'citta', 'provincia', 'lat', 'lng', 'note',
      'payment_methods', 'delivery_methods', 'privacy_policy', 'dichiarazione_veridicita',
      // Sede preferita
      'sede_preferita',
      // Foto profilo cliente
      'foto_profilo'
    ];
    
    const filteredResult = Object.keys(result)
      .filter(key => allowedFields.includes(key))
      .reduce((obj: any, key) => {
        obj[key] = (result as any)[key];
        return obj;
      }, {});
    
    console.log('🚀 [mapFormToDbData] Final filtered result:', filteredResult);
    
    return filteredResult;
  };

  // useEffect per inizializzare i dati quando initialData cambia
  useEffect(() => {
    console.log('🔄 [CustomerRegistrationForm] useEffect triggered, initialData:', initialData, 'isSedeLoading:', isSedeLoading, 'readOnly:', readOnly);

    // Verifica se ci sono dati iniziali validi PRIMA di controllare isSedeLoading
    // Se abbiamo dati validi, procedi comunque (la sede serve solo come fallback per form vuoti)
    const hasValidInitialData = initialData && Object.keys(initialData).length > 0 && Object.values(initialData).some(val => val !== null && val !== undefined && val !== '');

    // Se ci sono dati iniziali validi, mappali e impostali (indipendentemente da isSedeLoading)
    if (hasValidInitialData) {
      const mappedData = mapDbToFormData(initialData);
      console.log('📝 [CustomerRegistrationForm] Setting formData to:', mappedData);
      console.log('📍 [CustomerRegistrationForm] sedeCorrente usato come fallback:', sedeCorrente);

      // Traccia l'email originale per rilevare cambi (solo al primo caricamento)
      if (originalEmailRef.current === null && mappedData.email) {
        originalEmailRef.current = mappedData.email;
        console.log('📧 [CustomerRegistrationForm] Email originale tracciata:', mappedData.email);
      }

      setFormDataWithLog({...mappedData, userId: userId}); // Aggiungi userId per SessionStorage

      // Aggiorna il tipo form solo se non c'è già uno stato salvato in SessionStorage
      const savedType = sessionStorage.getItem(`toggleState_${userId}`);
      if (!savedType) {
        const dbFormType = mappedData.customer_type === 'company' ? 'azienda' : 'privato';
        setCurrentFormType(dbFormType);
        // Salva anche in SessionStorage per coerenza
        sessionStorage.setItem(`toggleState_${userId}`, dbFormType);
        console.log(`💾 [CustomerRegistrationForm] Salvato tipo iniziale da DB in SessionStorage: ${dbFormType}`);
      } else {
        console.log(`🔄 [CustomerRegistrationForm] Mantenuto tipo da SessionStorage: ${savedType}`);
      }
    } else {
      // Se non ci sono dati iniziali, imposta i default (solo se non già inizializzato)
      // In questo caso SERVE aspettare la sede per il valore di default sede_preferita
      if (isSedeLoading && !readOnly) {
        console.log('⏳ [CustomerRegistrationForm] Attendo caricamento sede per form nuovo (nessun dato iniziale)...');
        return;
      }

      if (!isInitializedRef.current) {
        console.log('📝 [CustomerRegistrationForm] No initialData valido, setting default formData');
        console.log('📝 [CustomerRegistrationForm] sedeCorrente per default:', sedeCorrente);

        setFormDataWithLog({
          indirizzo: { search: '', via: '', numeroCivico: '', citta: '', cap: '', provincia: '', paese: 'Italia' },
          prefissoNazione: 'IT',
          prefissoTelefono: '+39',
          referentePrincipalePrefissoFisso: '+39',
          referentePrincipalePrefissoCellulare: '+39',
          referenteSecondarioPrefissoFisso: '+39',
          referenteSecondarioPrefissoCellulare: '+39',
          referentePrivatoPrefissoFisso: '+39',
          referentePrivatoPrefissoCellulare: '+39',
          sede_preferita: sedeCorrente, // Sede corrente dall'header
          customer_type: currentFormType === 'azienda' ? 'company' : 'individual',
          userId: userId // Aggiungi userId per SessionStorage
        });
        isInitializedRef.current = true;
      }
    }
  }, [initialData, userId, sedeCorrente, isSedeLoading, readOnly]); // Aspetta che sede sia caricata (tranne in readOnly)
  
  // RIMOSSO: useEffect che causava loop infinito
  // useEffect(() => {
  //   console.log('🔄 [CustomerRegistrationForm] formData aggiornato:', formData);
  //   console.log('🔄 [CustomerRegistrationForm] formData.paymentMethods:', formData.paymentMethods);
  //   console.log('🔄 [CustomerRegistrationForm] formData.deliveryMethods:', formData.deliveryMethods);
  // }, [formData]);

  // Funzione che esegue effettivamente il salvataggio
  const performSubmit = async (values: Record<string, any>) => {
    try {
      console.log('🔄 [CustomerRegistrationForm] performSubmit chiamato con values:', values);


      if (!values || typeof values !== 'object') {
        throw new Error('Dati del form non validi');
      }

      const token = getToken?.();
      const dbData = mapFormToDbData(values);

      // 🔧 DEBUG COMPLETO: Verifica tutti i campi problematici
      console.log('🔧 [DEBUG] === SUBMIT DATA ANALYSIS ===');
      console.log('🔧 [DEBUG] Meccanico nel submit:', {
        website: values.meccanicoWebsite,
        prefisso: values.meccanicoPrefissoTelefono,
        social: values.meccanicoSocial,
        indirizzo: values.meccanicoIndirizzo
      });
      console.log('🔧 [DEBUG] Referente privato nel submit:', {
        prefissoFisso: values.referentePrivatoPrefissoFisso,
        prefissoCellulare: values.referentePrivatoPrefissoCellulare,
        telefonoFisso: values.referentePrivatoTelefonoFisso,
        cellulare: values.referentePrivatoCellulare
      });
      console.log('🔧 [DEBUG] Dati mappati per DB:', {
        meccanico_website: dbData.meccanico_website,
        meccanico_prefisso: dbData.meccanico_prefisso_telefono,
        meccanico_whatsapp: dbData.meccanico_whatsapp,
        meccanico_telegram: dbData.meccanico_telegram,
        referente1_telefono_prefisso: dbData.referente1_telefono_prefisso,
        referente1_cellulare_prefisso: dbData.referente1_cellulare_prefisso
      });


      // Determina quale endpoint usare - usa l'AuthProvider invece di localStorage
      const isAdminEditingOtherUser = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
      const isEditingOwnProfile = currentUser?.id === userId;

      console.log('🔍 [CustomerRegistrationForm] Debug endpoint selection:');
      console.log('  - currentUser:', currentUser);
      console.log('  - currentUser.role:', currentUser?.role);
      console.log('  - userId:', userId);
      console.log('  - isAdminEditingOtherUser:', isAdminEditingOtherUser);
      console.log('  - isEditingOwnProfile:', isEditingOwnProfile);
      console.log('  - currentUser.id === userId:', currentUser?.id === userId);
      console.log('  - currentUser.id:', currentUser?.id);
      console.log('  - userId type:', typeof userId);
      console.log('  - currentUser.id type:', typeof currentUser?.id);

      let endpoint, method;

      // Caso speciale: creazione nuovo cliente da trasferimento (userId = 'new-from-temp')
      const isCreatingNewCustomer = userId === 'new-from-temp';

      if (isCreatingNewCustomer) {
        // Admin crea nuovo cliente senza associazione utente
        endpoint = '/api/admin/profiles';
        method = 'POST';
        console.log('🆕 [CustomerRegistrationForm] Creazione NUOVO cliente da trasferimento');
      } else if (isAdminEditingOtherUser && !isEditingOwnProfile) {
        // Admin che modifica profilo di altro utente
        endpoint = `/api/admin/profiles/${userId}`;
        method = 'PUT';
        console.log('🔑 [CustomerRegistrationForm] Salvataggio ADMIN per altro utente:', userId);
      } else {
        // Utente che compila il suo primo profilo o admin che modifica il suo
        endpoint = '/api/profiles/me';
        method = (initialData && Object.keys(initialData).length > 0) ? 'PUT' : 'POST';
        console.log('👤 [CustomerRegistrationForm] Salvataggio UTENTE per profilo personale:', method);
      }

      const res = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(currentUser?.id ? { 'x-user-id': currentUser.id } : {}),
        },
        body: JSON.stringify(dbData),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.details || err.error || `Errore salvataggio (${res.status})`);
      }

      // Parsa la response per ottenere userId/profileId creati
      const responseData = await res.json().catch(() => ({}));
      const savedUserId = responseData.userId || userId; // Usa userId dalla response o quello esistente

      console.log('✅ [CustomerRegistrationForm] Salvataggio completato, chiamando onSuccess...');
      console.log('✅ [CustomerRegistrationForm] savedUserId dalla response:', savedUserId);

      // Il cliente e' salvato: i dati di fatturazione mancanti non bloccano, ma
      // vanno detti adesso — a salvataggio avvenuto nessuno tornerebbe a
      // cercarli, e senza si scopre il problema solo al momento della fattura.
      const mancanti: string[] = Array.isArray(responseData.warnings) ? responseData.warnings : [];
      if (mancanti.length > 0) {
        await appAlert(
          `Il cliente è stato salvato, ma mancano dati necessari alla fatturazione:\n\n` +
          mancanti.map(m => `  •  ${m}`).join('\n') +
          `\n\nPuoi aggiungerli in seguito con "Modifica cliente".`,
          { title: '⚠️ Dati fatturazione incompleti', confirmLabel: 'Ho capito' }
        );
      }

      onSuccess('Salvataggio completato.', savedUserId);
      console.log('✅ [CustomerRegistrationForm] onSuccess chiamato!');
    } catch (error: any) {
      onError(error?.message || error);
    }
  };

  // Handler principale submit - controlla email changes e sede prima di salvare
  const handleSubmit = async (values: Record<string, any>) => {
    // 1. Controlla se l'email è cambiata (solo per utenti con auth_type='email')
    const newEmail = values.email?.trim();
    const oldEmail = originalEmailRef.current?.trim();
    const emailChanged = oldEmail && newEmail && oldEmail !== newEmail;

    console.log('📧 [CustomerRegistrationForm] Email check:', { oldEmail, newEmail, emailChanged, authType: currentUser?.auth_type });

    if (emailChanged && currentUser?.auth_type === 'email') {
      // Utente email/password: mostra modal cambio email con verifica
      console.log('📧 [CustomerRegistrationForm] Email cambiata, mostro modal verifica');
      setPendingEmailChange({
        oldEmail: oldEmail,
        newEmail: newEmail,
        pendingFormValues: values
      });
      setShowEmailChangeModal(true);
      return;
    }

    // 2. Controlla se la sede preferita del cliente è diversa dalla sede selezionata nell'header
    if (values.sede_preferita && sedeCorrente && values.sede_preferita !== sedeCorrente) {
      console.log('⚠️ [CustomerRegistrationForm] Sede mismatch rilevato:', {
        sedePreferita: values.sede_preferita,
        sedeCorrente: sedeCorrente
      });
      setPendingSubmitValues(values);
      setShowSedeWarning(true);
      return;
    }
    // Nessun controllo pendente, procedi con il salvataggio
    await performSubmit(values);
  };

  // Handler per successo cambio email
  const handleEmailChangeSuccess = async () => {
    console.log('📧 [CustomerRegistrationForm] Email change request inviata con successo');
    setShowEmailChangeModal(false);

    if (pendingEmailChange) {
      // Aggiorna l'email originale con la nuova (verrà confermata via link)
      // ma procedi comunque con il salvataggio del resto dei dati
      // L'email nel DB non cambia finché l'utente non conferma via link
      const valuesToSave = { ...pendingEmailChange.pendingFormValues };
      // IMPORTANTE: Mantieni l'email originale nel salvataggio perché non è ancora confermata
      valuesToSave.email = pendingEmailChange.oldEmail;

      setPendingEmailChange(null);

      // Procedi con il controllo sede
      if (valuesToSave.sede_preferita && sedeCorrente && valuesToSave.sede_preferita !== sedeCorrente) {
        setPendingSubmitValues(valuesToSave);
        setShowSedeWarning(true);
        return;
      }

      await performSubmit(valuesToSave);
    }
  };

  // Handler per chiusura modal cambio email
  const handleEmailChangeClose = () => {
    setShowEmailChangeModal(false);
    setPendingEmailChange(null);
  };

  // Handler per conferma salvataggio nonostante sede diversa
  const handleConfirmSedeSave = async () => {
    setShowSedeWarning(false);
    if (pendingSubmitValues) {
      await performSubmit(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
  };

  // Handler per annullamento salvataggio
  const handleCancelSedeSave = () => {
    setShowSedeWarning(false);
    setPendingSubmitValues(null);
  };

  console.log('🎯 [CustomerRegistrationForm] Passando a ModularForm - formData:', JSON.stringify(formData, null, 2));

  
  // Funzione helper per calcolare displayName dai dati form
  const computeDisplayName = (data: Record<string, any>): string => {
    if (data.ragioneSociale || data.ragione_sociale || data.company_name) {
      return data.ragioneSociale || data.ragione_sociale || data.company_name;
    }
    const nome = data.nome || data.first_name || '';
    const cognome = data.cognome || data.last_name || '';
    return [nome, cognome].filter(Boolean).join(' ') || 'Nuovo Cliente';
  };

  // Handler per il cambio valori che fa il merge invece di sostituire
  // OTTIMIZZATO: evita aggiornamenti inutili
  const handleValuesChange = (newValues: Record<string, any>) => {
    // Evita aggiornamenti se i valori sono identici
    const hasChanges = Object.keys(newValues).some(key => {
      const oldValue = formData[key];
      const newValue = newValues[key];
      return JSON.stringify(oldValue) !== JSON.stringify(newValue);
    });

    if (hasChanges) {
      console.log('📝 [CustomerRegistrationForm] onValuesChange - valori cambiati, aggiornando formData');

      // Merge dei nuovi valori
      const mergedData = { ...formData, ...newValues };
      setFormDataWithLog(mergedData);

      // Sincronizza header se i campi rilevanti per il nome sono cambiati
      const nameFields = ['nome', 'cognome', 'ragioneSociale', 'first_name', 'last_name', 'company_name', 'customer_type'];
      const nameFieldChanged = nameFields.some(field => newValues[field] !== undefined);

      if (nameFieldChanged && onHeaderSync) {
        const displayName = computeDisplayName(mergedData);
        const customerType = mergedData.customer_type === 'company' ? 'company' : 'individual';
        console.log('🔄 [CustomerRegistrationForm] Sincronizzazione header:', { displayName, customerType });
        onHeaderSync({ displayName, customerType });
      }
    } else {
      console.log('📝 [CustomerRegistrationForm] onValuesChange - nessun cambiamento, skip aggiornamento');
    }
  };
  
  // Handler per il cambio tipo form che sincronizza lo stato locale
  const handleFormTypeChange = (newType: 'azienda' | 'privato') => {
    console.log(`🔄 [CustomerRegistrationForm] onFormTypeChange: ${currentFormType} → ${newType}`);
    setCurrentFormType(newType);

    // Aggiorna anche customer_type nei formData tramite onValuesChange
    const updatedData = {
      ...formData,
      customer_type: newType === 'azienda' ? 'company' : 'individual',
      userId: userId
    };

    setFormDataWithLog(updatedData);

    // Sincronizza header con il nuovo tipo
    if (onHeaderSync) {
      const displayName = computeDisplayName(updatedData);
      const customerType = newType === 'azienda' ? 'company' : 'individual';
      console.log('🔄 [CustomerRegistrationForm] Sincronizzazione header per cambio tipo:', { displayName, customerType });
      onHeaderSync({ displayName, customerType });
    }
  };
  
  // Handler per cambio foto profilo
  const handlePhotoChange = (newPhotoUrl: string | null) => {
    setFormDataWithLog((prev: Record<string, any>) => ({
      ...prev,
      fotoProfilo: newPhotoUrl
    }));
    // Notifica il parent del cambio foto per sincronizzazione header
    if (onPhotoChange) {
      onPhotoChange(newPhotoUrl);
    }
  };

  // ============================================
  // DevSimulator Integration (Global Floating Button)
  // ============================================

  // Funzione per generare dati azienda (usa nomi camelCase del form)
  const generateCompanyData = useCallback((options: Record<string, string>): Record<string, any> => {
    console.log('🔍 [generateCompanyData] Options ricevute:', options);
    const fillMode = options.fillMode || 'complete'; // CAMBIATO: fallback a 'complete' invece di 'required'
    console.log('🔍 [generateCompanyData] fillMode effettivo:', fillMode);
    const sedePreferita = options.sedePreferita || 'misterbianco';
    const userInfo = {
      email: currentUser?.email,
      name: currentUser?.name?.split(' ')[0],
      surname: currentUser?.name?.split(' ').slice(1).join(' ')
    };

    const baseData: Record<string, any> = {
      // === Tipo cliente ===
      customer_type: 'company',

      // === Dati Azienda OBBLIGATORI ===
      ragioneSociale: 'Azienda Test Simulazione SRL',
      prefissoNazione: 'IT',
      partitaIva: '12345678903',
      codiceUnivoco: 'ABC1234',
      pec: 'azienda.test@pec.it',
      email: userInfo.email || 'test@example.com',
      prefissoTelefono: '+39',
      telefono: '0912345678',
      telefonoSocial: { whatsapp: false, telegram: false },

      // === Referente Principale OBBLIGATORI ===
      referentePrincipaleNome: userInfo.name || 'Mario',
      referentePrincipaleCognome: userInfo.surname || 'Rossi',
      referentePrincipaleEmail: userInfo.email || 'mario.rossi@example.com',

      // === Sede Preferita ===
      sede_preferita: sedePreferita,

      // === Privacy OBBLIGATORI ===
      privacyAccepted: true,
      dichiarazioneVeridicita: true,
    };

    if (fillMode === 'complete') {
      Object.assign(baseData, {
        // Telefono social
        telefonoSocial: { whatsapp: true, telegram: true },

        // Referente Principale completo
        referentePrincipaleTitolo: 'sig',
        referentePrincipalePrefissoFisso: '+39',
        referentePrincipaleTelefono: '0912345678',
        referentePrincipalePrefissoCellulare: '+39',
        referentePrincipaleCellulare: '3331234567',
        referentePrincipaleSocial: { whatsapp: true, telegram: true },

        // Referente Secondario
        referenteSecondarioTitolo: 'dott',
        referenteSecondarioNome: 'Luigi',
        referenteSecondarioCognome: 'Verdi',
        referenteSecondarioEmail: 'luigi.verdi@example.com',
        referenteSecondarioPrefissoFisso: '+39',
        referenteSecondarioTelefono: '0987654321',
        referenteSecondarioPrefissoCellulare: '+39',
        referenteSecondarioCellulare: '3339876543',
        referenteSecondarioSocial: { whatsapp: true, telegram: true },

        // Indirizzo (oggetto)
        indirizzo: {
          via: 'Via Roma',
          numeroCivico: '123',
          cap: '95100',
          citta: 'Catania',
          provincia: 'CT',
          paese: 'Italia'
        },
        noteIndirizzo: 'Campanello secondo piano, interno 5. Portone con codice 1234.',

        // Meccanico di fiducia
        meccanicoRagioneSociale: 'Officina Test SNC',
        meccanicoNome: 'Giuseppe',
        meccanicoCognome: 'Bianchi',
        meccanicoEmail: 'officina@test.it',
        meccanicoWebsite: 'https://www.officinatest.it',
        meccanicoPrefissoTelefono: '+39',
        meccanicoTelefono: '0951234567',
        meccanicoSocial: { whatsapp: true, telegram: true },
        meccanicoIndirizzo: {
          via: 'Via Etnea',
          numeroCivico: '456',
          cap: '95100',
          citta: 'Catania',
          provincia: 'CT',
          paese: 'Italia'
        },

        // Metodi di pagamento (IDs devono matchare PaymentMethodsCustom)
        paymentMethods: {
          methods: ['office', 'paypal', 'transfer', 'cash', 'pos', 'invoice', 'sepa', 'financing'],
          preferences: {
            preferredCard: 'visa',
            bankDetails: 'IBAN IT60X0542811101000000654321 - Banca Aziendale SPA',
            invoiceTerms: 30,
            financingProvider: 'Findomestic',
            notes: 'Preferenza pagamento: bonifico anticipato. Per ordini > 5000€ possibile pagamento differito.'
          }
        },

        // Metodi di consegna (IDs devono matchare DeliveryMethodsCustom)
        // IDs corretti: pickup, courier, company_delivery, customer_pickup, express, special, pickup_point
        deliveryMethods: {
          methods: ['pickup', 'courier', 'company_delivery', 'customer_pickup', 'express', 'special', 'pickup_point'],
          preferences: {
            courierName: 'DHL Express',
            expressType: '24h',
            // Campo UI: preferredAddress (non deliveryAddress)
            preferredAddress: 'Via Roma, 123 - 95100 Catania CT',
            // Campo UI: timeSlot (non deliveryTimePreference)
            timeSlot: 'Pomeriggio (14:00-18:00)',
            pickupPointAddress: 'Punto DHL - Via Etnea 100, Catania',
            specialInstructions: 'Materiale fragile, maneggiare con cura. Peso max 50kg per collo.',
            notes: 'Preferenza: ritiro in sede Misterbianco. Per urgenze accettiamo corriere express.'
          }
        },
      });
    }

    return baseData;
  }, [currentUser]);

  // Funzione per generare dati privato (usa nomi camelCase del form)
  const generateIndividualData = useCallback((options: Record<string, string>): Record<string, any> => {
    console.log('🔍 [generateIndividualData] Options ricevute:', options);
    const fillMode = options.fillMode || 'complete'; // CAMBIATO: fallback a 'complete' invece di 'required'
    console.log('🔍 [generateIndividualData] fillMode effettivo:', fillMode);
    const sedePreferita = options.sedePreferita || 'misterbianco';
    const userInfo = {
      email: currentUser?.email,
      name: currentUser?.name?.split(' ')[0],
      surname: currentUser?.name?.split(' ').slice(1).join(' ')
    };

    const baseData: Record<string, any> = {
      // === Tipo cliente ===
      customer_type: 'individual',

      // === Dati Privato OBBLIGATORI ===
      nome: userInfo.name || 'Salvatore',
      cognome: userInfo.surname || 'De Luca',
      codiceFiscale: 'DLCSVT58C16C351W',
      email: userInfo.email || 'salvatore.deluca@example.com',
      prefissoTelefono: '+39',
      telefono: '3331234567',
      // Il form privato usa 'social' invece di 'telefonoSocial'
      social: { whatsapp: false, telegram: false },
      telefonoSocial: { whatsapp: false, telegram: false },

      // === Sede Preferita ===
      sede_preferita: sedePreferita,

      // === Privacy OBBLIGATORI ===
      privacyAccepted: true,
      dichiarazioneVeridicita: true,
    };

    if (fillMode === 'complete') {
      Object.assign(baseData, {
        // Titolo e PEC
        titolo: 'sig',
        pec: 'salvatore.deluca@pec.it',

        // Telefono social (privato usa 'social')
        social: { whatsapp: true, telegram: true },
        telefonoSocial: { whatsapp: true, telegram: true },

        // Referente Privato (equivalente al principale per privato)
        referentePrivatoTitolo: 'sig',
        referentePrivatoNome: userInfo.name || 'Salvatore',
        referentePrivatoCognome: userInfo.surname || 'De Luca',
        referentePrivatoEmail: userInfo.email || 'salvatore.deluca@example.com',
        referentePrivatoPrefissoFisso: '+39',
        referentePrivatoTelefonoFisso: '0951234567',
        referentePrivatoPrefissoCellulare: '+39',
        referentePrivatoCellulare: '3331234567',
        referentePrivatoSocial: { whatsapp: true, telegram: true },

        // Indirizzo (oggetto)
        indirizzo: {
          via: 'Via Etnea',
          numeroCivico: '789',
          cap: '95100',
          citta: 'Catania',
          provincia: 'CT',
          paese: 'Italia'
        },
        noteIndirizzo: 'Citofono De Luca, terzo piano. Disponibile dalle 9 alle 18.',

        // Meccanico di fiducia
        meccanicoRagioneSociale: 'Officina Personale SRL',
        meccanicoNome: 'Antonio',
        meccanicoCognome: 'Neri',
        meccanicoEmail: 'officina.personale@test.it',
        meccanicoWebsite: 'https://www.officinapersonale.it',
        meccanicoPrefissoTelefono: '+39',
        meccanicoTelefono: '0959876543',
        meccanicoSocial: { whatsapp: true, telegram: true },
        meccanicoIndirizzo: {
          via: 'Corso Italia',
          numeroCivico: '100',
          cap: '95100',
          citta: 'Catania',
          provincia: 'CT',
          paese: 'Italia'
        },

        // Metodi di pagamento (IDs devono matchare PaymentMethodsCustom)
        paymentMethods: {
          methods: ['office', 'paypal', 'transfer', 'cash', 'pos', 'invoice', 'sepa', 'financing'],
          preferences: {
            preferredCard: 'mastercard',
            bankDetails: `IBAN IT60X0542811101000000123456 - Intestato a ${userInfo.name && userInfo.surname ? `${userInfo.name} ${userInfo.surname}` : 'Salvatore De Luca'}`,
            invoiceTerms: 30,
            financingProvider: 'Compass',
            notes: 'Preferenza pagamento: contanti o PayPal. Per importi elevati disponibile bonifico.'
          }
        },

        // Metodi di consegna (IDs devono matchare DeliveryMethodsCustom)
        // IDs corretti: pickup, courier, company_delivery, customer_pickup, express, special, pickup_point
        deliveryMethods: {
          methods: ['pickup', 'courier', 'company_delivery', 'customer_pickup', 'express', 'special', 'pickup_point'],
          preferences: {
            courierName: 'Bartolini BRT',
            expressType: '48h',
            // Campo UI: preferredAddress (non deliveryAddress)
            preferredAddress: 'Via Etnea, 789 - 95100 Catania CT',
            // Campo UI: timeSlot (non deliveryTimePreference)
            timeSlot: 'Mattina (9:00-12:00)',
            pickupPointAddress: 'Fermopoint - Tabaccheria Via Roma 50, Catania',
            specialInstructions: 'Consegna al piano terra. Citofono: De Luca.',
            notes: 'Preferenza: ritiro in sede. Se non disponibile, spedizione con corriere a casa.'
          }
        },
      });
    }

    return baseData;
  }, [currentUser]);

  // Funzione RICORSIVA per verificare se un valore è "vuoto"
  // (include oggetti con valori falsy annidati a qualsiasi livello)
  const isEmptyValue = (value: any): boolean => {
    // Casi base: null, undefined, stringa vuota
    if (value === null || value === undefined || value === '') return true;

    // Boolean false è considerato "vuoto" per i nostri scopi (es. whatsapp: false)
    if (value === false) return true;

    // Array vuoto
    if (Array.isArray(value)) return value.length === 0;

    // Oggetto: controlla RICORSIVAMENTE tutti i valori
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return true;
      // Un oggetto è "vuoto" se TUTTI i suoi valori sono "vuoti" (ricorsione)
      return keys.every(k => isEmptyValue(value[k]));
    }

    // Qualsiasi altro valore (numero, stringa non vuota, boolean true) non è vuoto
    return false;
  };

  // Funzione per DEEP MERGE: sostituisce solo i campi vuoti a qualsiasi livello di annidamento
  const deepMergePreserving = (existing: any, newData: any, path: string = ''): any => {
    // DEBUG: Log specifico per campo titolo
    if (path === 'titolo') {
      console.log(`🧪 [DeepMerge] DEBUG TITOLO:`, {
        path,
        existing,
        existingType: typeof existing,
        isExistingEmpty: isEmptyValue(existing),
        newData,
        newDataType: typeof newData,
        isNewDataEmpty: isEmptyValue(newData)
      });
    }

    // Se il valore esistente è vuoto, usa il nuovo valore
    if (isEmptyValue(existing)) {
      console.log(`🧪 [DeepMerge] ${path || 'root'}: existing è vuoto, uso newData`);
      return newData;
    }

    // Se il nuovo valore è vuoto, mantieni l'esistente
    if (isEmptyValue(newData)) {
      console.log(`🧪 [DeepMerge] ${path || 'root'}: newData è vuoto, mantengo existing`);
      return existing;
    }

    // Se entrambi sono oggetti (non array), fai deep merge
    if (
      typeof existing === 'object' && existing !== null && !Array.isArray(existing) &&
      typeof newData === 'object' && newData !== null && !Array.isArray(newData)
    ) {
      const result = { ...existing };
      for (const key of Object.keys(newData)) {
        const childPath = path ? `${path}.${key}` : key;
        result[key] = deepMergePreserving(existing[key], newData[key], childPath);
      }
      return result;
    }

    // Per array: se l'esistente è vuoto, usa il nuovo; altrimenti mantieni l'esistente
    // (gli array come methods[] non vengono merged elemento per elemento)
    if (Array.isArray(existing) && Array.isArray(newData)) {
      if (existing.length === 0) {
        console.log(`🧪 [DeepMerge] ${path}: array esistente vuoto, uso newData array`);
        return newData;
      }
      console.log(`🧪 [DeepMerge] ${path}: array esistente non vuoto, mantengo`);
      return existing;
    }

    // Per valori primitivi: mantieni l'esistente (non vuoto)
    console.log(`🧪 [DeepMerge] ${path}: valore primitivo esistente non vuoto, mantengo`);
    return existing;
  };

  // Funzione per applicare i dati simulati al form
  const applySimulatedData = useCallback((data: Record<string, any>, _options?: ApplyDataOptions) => {
    // Legge l'opzione preserveExisting dalle opzioni passate dal pannello
    const preserveExisting = data._preserveExisting === 'true';

    // Rimuovi il flag interno dai dati
    const simulatedData = { ...data };
    delete simulatedData._preserveExisting;

    console.log('🧪 [DevSimulator] ========== INIZIO APPLICAZIONE DATI ==========');
    console.log('🧪 [DevSimulator] Dati simulati COMPLETI:', JSON.stringify(simulatedData, null, 2));
    console.log('🧪 [DevSimulator] Preserva dati esistenti:', preserveExisting);
    console.log('🧪 [DevSimulator] Campi simulati:', Object.keys(simulatedData));

    // DEBUG: Log specifici per i campi problematici
    const campiCritici = ['titolo', 'paymentMethods', 'deliveryMethods', 'meccanicoIndirizzo',
                          'referentePrincipaleSocial', 'referenteSecondarioSocial',
                          'meccanicoSocial', 'privacyAccepted', 'dichiarazioneVeridicita'];
    console.log('🧪 [DevSimulator] Valori campi critici generati:');
    campiCritici.forEach(campo => {
      console.log(`   - ${campo}:`, simulatedData[campo]);
    });
    // DEBUG EXTRA: Campo titolo
    console.log('🧪 [DevSimulator] DEBUG TITOLO:', {
      titolo: simulatedData.titolo,
      customer_type: simulatedData.customer_type,
      fillMode: data._fillMode
    });

    // Funzione helper per applicare i dati al form
    const applyDataToForm = (data: Record<string, any>, isPreserve: boolean, prevFormData?: Record<string, any>) => {
      if (isPreserve) {
        setFormDataWithLog((prev: Record<string, any>) => {
          const currentPrev = prevFormData || prev;
          console.log('🧪 [DevSimulator] FormData PRECEDENTE:', JSON.stringify(currentPrev, null, 2));

          // Usa DEEP MERGE per preservare i campi non vuoti a ogni livello
          const merged = deepMergePreserving(currentPrev, data);

          console.log('🧪 [DevSimulator] FormData FINALE (deep merged):', JSON.stringify(merged, null, 2));
          console.log('🧪 [DevSimulator] ========== FINE APPLICAZIONE DATI ==========');

          return merged;
        });
      } else {
        console.log('🧪 [DevSimulator] Modalità SOSTITUZIONE TOTALE (preserveExisting=false)');
        setFormDataWithLog((prev: Record<string, any>) => {
          const currentPrev = prevFormData || prev;
          const newData = {
            ...data,
            fotoProfilo: currentPrev.fotoProfilo || data.fotoProfilo || null
          };
          console.log('🧪 [DevSimulator] FormData NUOVO:', JSON.stringify(newData, null, 2));
          return newData;
        });
      }
    };

    // Gestione sincronizzazione tipo form
    // IMPORTANTE: Il tipo viene SEMPRE cambiato se diverso dal corrente.
    // preserveExisting controlla solo se i VALORI esistenti vengono preservati (deep merge vs replace)
    console.log(`🧪 [DevSimulator] DEBUG CAMBIO TIPO:`, {
      preserveExisting,
      currentFormType,
      customer_type_generato: simulatedData.customer_type
    });

    const generatedCustomerType = simulatedData.customer_type;
    console.log(`🧪 [DevSimulator] generatedCustomerType:`, generatedCustomerType, `tipo:`, typeof generatedCustomerType);

    if (generatedCustomerType) {
      const newFormType = generatedCustomerType === 'company' ? 'azienda' : 'privato';
      console.log(`🧪 [DevSimulator] Sincronizzazione tipo form: ${currentFormType} → ${newFormType}`);
      console.log(`🧪 [DevSimulator] Condizione: "${newFormType}" !== "${currentFormType}" = ${newFormType !== currentFormType}`);

      if (newFormType !== currentFormType) {
        // IMPORTANTE: Quando cambia il tipo form, dobbiamo:
        // 1. Prima cambiare il tipo form
        // 2. POI applicare i dati DOPO che React ha ri-renderizzato il form
        // Questo evita il batching di React che causerebbe l'applicazione dei dati
        // al form vecchio invece che a quello nuovo
        console.log(`🧪 [DevSimulator] Cambio tipo form rilevato, uso setTimeout per sincronizzare`);
        setCurrentFormType(newFormType);
        // Salva anche in SessionStorage per persistenza
        sessionStorage.setItem(`toggleState_${userId}`, newFormType);

        // Applica i dati DOPO il re-render del form con setTimeout
        // Usiamo 100ms per essere sicuri che React abbia completato il remount del form
        // (il form usa key={formType} che causa un full remount)
        // preserveExisting controlla se fare merge o replace dei valori
        setTimeout(() => {
          console.log(`🧪 [DevSimulator] setTimeout eseguito, applico dati al form ${newFormType} (preserveExisting=${preserveExisting})`);
          applyDataToForm(simulatedData, preserveExisting);
        }, 100);
        return; // Esce, i dati verranno applicati dal setTimeout
      }
    }

    // Applica i dati normalmente (stesso tipo form)
    applyDataToForm(simulatedData, preserveExisting);
  }, [currentFormType, userId]);

  // Configurazione del DevSimulator per questo form
  const devSimulatorConfig = useMemo<SimulatorFormConfig>(() => ({
    id: 'customer-registration-form',
    displayName: 'Profilo Cliente',
    description: 'Genera dati di test per il form registrazione cliente',
    options: [
      {
        id: 'customerType',
        label: 'Tipo Cliente',
        type: 'select',
        values: [
          { value: 'company', label: '🏢 Azienda' },
          { value: 'individual', label: '👤 Privato' }
        ],
        defaultValue: 'company'
      },
      {
        id: 'fillMode',
        label: 'Campi da Compilare',
        type: 'select',
        values: [
          { value: 'complete', label: '📝 Tutti i Campi (Recommended)' },
          { value: 'required', label: '📋 Solo Obbligatori' }
        ],
        defaultValue: 'complete'
      },
      {
        id: 'sedePreferita',
        label: 'Sede Preferita',
        type: 'select',
        values: [
          { value: 'misterbianco', label: '🔵 Misterbianco' },
          { value: 'palermo', label: '🟠 Palermo' }
        ],
        defaultValue: sedeCorrente // Sincronizzato con header dropdown
      },
      {
        id: 'preserveExisting',
        label: 'Preserva dati esistenti',
        type: 'toggle',
        defaultValue: 'true'
      }
    ],
    generateData: (selectedOptions) => {
      // IMPORTANTE: preserveExisting significa "preserva i VALORI esistenti",
      // NON "preserva il TIPO esistente". Il tipo viene SEMPRE determinato dal selettore.
      const customerType = selectedOptions.customerType || 'company';
      console.log(`🧪 [DevSimulator] Tipo selezionato: ${customerType}, preserveExisting=${selectedOptions.preserveExisting}`);
      console.log(`🧪 [DevSimulator] selectedOptions COMPLETE:`, JSON.stringify(selectedOptions, null, 2));

      const data = customerType === 'company'
        ? generateCompanyData(selectedOptions)
        : generateIndividualData(selectedOptions);

      // Aggiungi il flag preserveExisting ai dati per passarlo a applyData
      return { ...data, _preserveExisting: selectedOptions.preserveExisting || 'true' };
    },
    applyData: applySimulatedData
  }), [generateCompanyData, generateIndividualData, applySimulatedData, currentFormType, sedeCorrente]);

  // Registra il form nel sistema globale DevSimulator
  useDevSimulatorForm(devSimulatorConfig);

  // Recupera nome cliente per label foto
  const getCustomerDisplayName = () => {
    if (formData.ragioneSociale || formData.ragione_sociale) {
      return formData.ragioneSociale || formData.ragione_sociale;
    }
    const nome = formData.nomeReferente1 || formData.nome_referente_1 || formData.nome || '';
    const cognome = formData.cognomeReferente1 || formData.cognome_referente_1 || formData.cognome || '';
    return [nome, cognome].filter(Boolean).join(' ') || 'Nuovo Cliente';
  };

  // Estrai customer_code per QR (se cliente esistente)
  const customerCode = formData.customer_code || initialData?.customer_code || null;

  // Componente foto profilo da passare al ModularForm
  const photoInputComponent = (
    <ProfilePhotoInputWithRemote
      value={formData.fotoProfilo || null}
      onChange={handlePhotoChange}
      customerName={getCustomerDisplayName()}
      customerId={userId}
      userId={currentUser?.id?.toString() || 'unknown'}
      userName={currentUser?.name || 'Operatore'}
      label="Foto Profilo Cliente"
      helperText="Scatta o carica una foto del cliente per facilitare il riconoscimento"
      required={false}
    />
  );

  // Componente QR code cliente (solo se esiste customer_code)
  const qrSectionComponent = customerCode ? (
    <CustomerQRSection
      customerCode={customerCode}
      customerName={getCustomerDisplayName()}
    />
  ) : null;

  return (
    <div data-form-loaded={initialData && Object.keys(initialData).length > 0 ? "true" : "false"}>
      {/* DevSimulator globale - pulsante floating in basso a sinistra (gestito da App.tsx) */}

      <ModularForm
        config={customerRegistrationConfig}
        initialValues={formData}
        onSubmit={handleSubmit}
        onValuesChange={handleValuesChange}
        onFormTypeChange={handleFormTypeChange}
        showTypeSwitch={showTypeSwitch}
        defaultType={currentFormType}
        readOnly={readOnly}
        // Per utenti NON client: salta validazione required ma mostra popup conferma
        skipRequiredValidation={!isClientUser}
        onMissingRequiredFields={!isClientUser ? handleMissingRequiredFields : undefined}
        // Foto profilo nella sezione Tipo Cliente (colonna sinistra)
        typeSwitchLeftContent={photoInputComponent}
        // QR code cliente sotto la sezione Tipo Cliente (se cliente esistente)
        typeSwitchBottomContent={qrSectionComponent}
        hideSubmitButton={hideSubmitButton}
      />

      {/* Modal conferma campi mancanti */}
      <MissingFieldsWarningModal
        isOpen={missingFieldsModal.isOpen}
        missingFields={missingFieldsModal.fields}
        title="Attenzione: campi importanti mancanti"
        subtitle="Puoi salvare, ma il cliente sarà difficile da rintracciare"
        closeLabel="Torna a compilare"
        proceedLabel="Procedi comunque"
        onClose={handleModalCancel}
        onProceed={handleModalConfirm}
      />

      {/* Sede Warning Modal */}
      {showSedeWarning && (
        <>
          <div className="fixed inset-0 bg-black/70 z-[60]" onClick={handleCancelSedeSave} />
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="bg-zinc-800 rounded-xl shadow-2xl border border-amber-500/50 max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-amber-500/20 rounded-full">
                  <MapPin className="w-6 h-6 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Sede diversa da header</h3>
              </div>
              <p className="text-gray-300 mb-2">
                La <strong>sede preferita</strong> del cliente (<span className="text-amber-400">{pendingSubmitValues?.sede_preferita && SEDI[pendingSubmitValues.sede_preferita as SedeType]?.nome}</span>) è diversa dalla sede attualmente selezionata nell'header (<span className="text-blue-400">{sedeCorrente && SEDI[sedeCorrente]?.nome}</span>).
              </p>
              <p className="text-gray-400 text-sm mb-6">
                Vuoi procedere comunque con il salvataggio?
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancelSedeSave}
                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  Annulla
                </button>
                <button
                  onClick={handleConfirmSedeSave}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Salva comunque
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Email Change Modal - per utenti con autenticazione email/password */}
      {pendingEmailChange && (
        <EmailChangeModal
          isOpen={showEmailChangeModal}
          onClose={handleEmailChangeClose}
          currentEmail={pendingEmailChange.oldEmail}
          newEmail={pendingEmailChange.newEmail}
          userId={userId}
          authType={currentUser?.auth_type || 'email'}
          onSuccess={handleEmailChangeSuccess}
          onError={(error) => {
            console.error('📧 [CustomerRegistrationForm] Errore cambio email:', error);
            onError(error);
          }}
        />
      )}
    </div>
  );
};